import React, { useState } from 'react';
import { motion } from 'framer-motion';
import DashboardLayout from '../../components/dashboard/DashboardLayout';
import SafeIcon from '../../common/SafeIcon';
import Button from '../../components/common/Button';
import * as FiIcons from 'react-icons/fi';

const { 
  FiShoppingBag, FiPlus, FiDollarSign, FiPackage, FiImage, 
  FiShoppingCart, FiPercent, FiUsers, FiBarChart2, FiPrinter,
  FiDownload, FiTrash2, FiEdit, FiEye, FiGrid, FiList
} = FiIcons;

const StoreManager = () => {
  const [activeTab, setActiveTab] = useState('products');
  const [viewMode, setViewMode] = useState('grid');

  const products = [
    {
      id: 1,
      name: 'Digital Download - Full Resolution',
      type: 'digital',
      price: 25,
      image: 'https://images.unsplash.com/photo-1542038784456-1ea8e935640e?w=300&h=300&fit=crop',
      sales: 48,
      inStock: true
    },
    {
      id: 2,
      name: '8x10 Matte Print',
      type: 'print',
      price: 15,
      image: 'https://images.unsplash.com/photo-1594909122845-11baa439b7bf?w=300&h=300&fit=crop',
      sales: 32,
      inStock: true
    },
    {
      id: 3,
      name: 'Canvas Print 16x20',
      type: 'print',
      price: 75,
      image: 'https://images.unsplash.com/photo-1579762593175-20226054cad0?w=300&h=300&fit=crop',
      sales: 12,
      inStock: true
    },
    {
      id: 4,
      name: 'Photo Album - Premium',
      type: 'physical',
      price: 120,
      image: 'https://images.unsplash.com/photo-1518893494013-481c1d8ed3fd?w=300&h=300&fit=crop',
      sales: 8,
      inStock: false
    },
    {
      id: 5,
      name: 'Metal Print 20x30',
      type: 'print',
      price: 150,
      image: 'https://images.unsplash.com/photo-1621619856624-92058c7e8da4?w=300&h=300&fit=crop',
      sales: 5,
      inStock: true
    },
    {
      id: 6,
      name: 'Digital Package - Web Optimized',
      type: 'digital',
      price: 35,
      image: 'https://images.unsplash.com/photo-1558591710-4b4a1ae0f04d?w=300&h=300&fit=crop',
      sales: 27,
      inStock: true
    }
  ];

  const orders = [
    {
      id: 'ORD-12345',
      customer: 'Sarah Johnson',
      date: '2024-01-10',
      items: 3,
      total: 95.00,
      status: 'completed',
      payment: 'paid'
    },
    {
      id: 'ORD-12346',
      customer: 'Mike Williams',
      date: '2024-01-09',
      items: 1,
      total: 150.00,
      status: 'processing',
      payment: 'paid'
    },
    {
      id: 'ORD-12347',
      customer: 'Emily Davis',
      date: '2024-01-08',
      items: 2,
      total: 40.00,
      status: 'shipped',
      payment: 'paid'
    },
    {
      id: 'ORD-12348',
      customer: 'Robert Smith',
      date: '2024-01-07',
      items: 5,
      total: 210.00,
      status: 'completed',
      payment: 'paid'
    }
  ];

  const discountCodes = [
    {
      code: 'SUMMER2024',
      discount: 20,
      type: 'percentage',
      used: 15,
      expires: '2024-06-30',
      status: 'active'
    },
    {
      code: 'WELCOME10',
      discount: 10,
      type: 'percentage',
      used: 32,
      expires: '2024-12-31',
      status: 'active'
    },
    {
      code: 'FREESHIP',
      discount: 15,
      type: 'fixed',
      used: 8,
      expires: '2024-03-15',
      status: 'expired'
    }
  ];

  const salesData = [
    { month: 'Jan', sales: 1200 },
    { month: 'Feb', sales: 900 },
    { month: 'Mar', sales: 1500 },
    { month: 'Apr', sales: 1800 },
    { month: 'May', sales: 1200 },
    { month: 'Jun', sales: 2100 }
  ];

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'processing':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'shipped':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400';
      case 'cancelled':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'expired':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  const getProductTypeIcon = (type) => {
    switch (type) {
      case 'digital':
        return FiDownload;
      case 'print':
        return FiPrinter;
      case 'physical':
        return FiPackage;
      default:
        return FiImage;
    }
  };

  const tabs = [
    { id: 'products', label: 'Products', icon: FiPackage },
    { id: 'orders', label: 'Orders', icon: FiShoppingCart },
    { id: 'discounts', label: 'Discounts', icon: FiPercent },
    { id: 'customers', label: 'Customers', icon: FiUsers },
    { id: 'analytics', label: 'Analytics', icon: FiBarChart2 }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'products':
        return (
          <div className="space-y-6">
            {/* Products Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Products
              </h2>
              <div className="mt-4 sm:mt-0 flex space-x-3">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-md ${
                      viewMode === 'grid'
                        ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                        : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <SafeIcon icon={FiGrid} className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-md ${
                      viewMode === 'list'
                        ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                        : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <SafeIcon icon={FiList} className="w-5 h-5" />
                  </button>
                </div>
                <Button icon={FiPlus}>
                  Add Product
                </Button>
              </div>
            </div>

            {/* Products Grid/List */}
            {viewMode === 'grid' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {products.map((product) => (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="bg-white dark:bg-dark-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
                  >
                    <div className="relative aspect-square bg-gray-200 dark:bg-gray-700">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-2 right-2 flex space-x-1">
                        <button className="p-1 bg-white dark:bg-dark-800 rounded-md shadow-md hover:bg-gray-100 dark:hover:bg-gray-700">
                          <SafeIcon icon={FiEye} className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                        </button>
                        <button className="p-1 bg-white dark:bg-dark-800 rounded-md shadow-md hover:bg-gray-100 dark:hover:bg-gray-700">
                          <SafeIcon icon={FiEdit} className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                        </button>
                      </div>
                      {!product.inStock && (
                        <div className="absolute top-2 left-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">
                          Out of Stock
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <div className="flex items-center mb-2">
                        <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mr-2">
                          <SafeIcon 
                            icon={getProductTypeIcon(product.type)} 
                            className="w-4 h-4 text-primary-600 dark:text-primary-400" 
                          />
                        </div>
                        <span className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                          {product.type}
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        {product.name}
                      </h3>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <SafeIcon icon={FiDollarSign} className="w-4 h-4 text-gray-500 dark:text-gray-400 mr-1" />
                          <span className="text-lg font-bold text-gray-900 dark:text-white">
                            ${product.price}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <SafeIcon icon={FiShoppingCart} className="w-4 h-4 text-gray-500 dark:text-gray-400 mr-1" />
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {product.sales} sold
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-750 border-b border-gray-200 dark:border-gray-700">
                      <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Product
                      </th>
                      <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Price
                      </th>
                      <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Sales
                      </th>
                      <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="py-3 px-4 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {products.map((product) => (
                      <tr 
                        key={product.id} 
                        className="hover:bg-gray-50 dark:hover:bg-gray-750"
                      >
                        <td className="py-4 px-4">
                          <div className="flex items-center">
                            <div className="w-10 h-10 rounded-md overflow-hidden mr-3">
                              <img
                                src={product.image}
                                alt={product.name}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                              {product.name}
                            </span>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center">
                            <div className="w-6 h-6 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mr-2">
                              <SafeIcon 
                                icon={getProductTypeIcon(product.type)} 
                                className="w-3 h-3 text-primary-600 dark:text-primary-400" 
                              />
                            </div>
                            <span className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                              {product.type}
                            </span>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center">
                            <SafeIcon icon={FiDollarSign} className="w-4 h-4 text-gray-500 dark:text-gray-400 mr-1" />
                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                              {product.price}
                            </span>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center">
                            <SafeIcon icon={FiShoppingCart} className="w-4 h-4 text-gray-500 dark:text-gray-400 mr-1" />
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                              {product.sales} sold
                            </span>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            product.inStock 
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                              : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                          }`}>
                            {product.inStock ? 'In Stock' : 'Out of Stock'}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-right">
                          <div className="flex justify-end space-x-2">
                            <button className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
                              <SafeIcon icon={FiEye} className="w-4 h-4" />
                            </button>
                            <button className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
                              <SafeIcon icon={FiEdit} className="w-4 h-4" />
                            </button>
                            <button className="p-1 text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400">
                              <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      
      case 'orders':
        return (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Orders
              </h2>
              <div className="mt-4 sm:mt-0">
                <Button variant="outline" icon={FiIcons.FiDownload}>
                  Export Orders
                </Button>
              </div>
            </div>
            
            <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-750 border-b border-gray-200 dark:border-gray-700">
                    <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Order ID
                    </th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Items
                    </th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Total
                    </th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="py-3 px-4 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {orders.map((order) => (
                    <tr 
                      key={order.id} 
                      className="hover:bg-gray-50 dark:hover:bg-gray-750"
                    >
                      <td className="py-4 px-4">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {order.id}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-sm text-gray-900 dark:text-white">
                          {order.customer}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {new Date(order.date).toLocaleDateString()}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {order.items}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center">
                          <SafeIcon icon={FiDollarSign} className="w-4 h-4 text-gray-500 dark:text-gray-400 mr-1" />
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {order.total.toFixed(2)}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(order.status)}`}>
                          {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-right">
                        <Button variant="outline" size="sm">
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      
      case 'discounts':
        return (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Discount Codes
              </h2>
              <div className="mt-4 sm:mt-0">
                <Button icon={FiPlus}>
                  Create Discount
                </Button>
              </div>
            </div>
            
            <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-750 border-b border-gray-200 dark:border-gray-700">
                    <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Code
                    </th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Discount
                    </th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Used
                    </th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Expires
                    </th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="py-3 px-4 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {discountCodes.map((discount) => (
                    <tr 
                      key={discount.code} 
                      className="hover:bg-gray-50 dark:hover:bg-gray-750"
                    >
                      <td className="py-4 px-4">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {discount.code}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-sm text-gray-900 dark:text-white">
                          {discount.type === 'percentage' ? `${discount.discount}%` : `$${discount.discount}`}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {discount.used} times
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {new Date(discount.expires).toLocaleDateString()}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(discount.status)}`}>
                          {discount.status.charAt(0).toUpperCase() + discount.status.slice(1)}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-right">
                        <div className="flex justify-end space-x-2">
                          <button className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
                            <SafeIcon icon={FiEdit} className="w-4 h-4" />
                          </button>
                          <button className="p-1 text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400">
                            <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      
      case 'analytics':
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Sales Analytics
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Total Revenue
                  </h3>
                  <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                    <SafeIcon icon={FiDollarSign} className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  </div>
                </div>
                <div className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                  $7,845
                </div>
                <div className="flex items-center text-sm">
                  <SafeIcon icon={FiIcons.FiArrowUp} className="w-4 h-4 text-green-500 mr-1" />
                  <span className="text-green-500 font-medium">12% increase</span>
                  <span className="text-gray-500 dark:text-gray-400 ml-1">vs last month</span>
                </div>
              </div>
              
              <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Orders
                  </h3>
                  <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                    <SafeIcon icon={FiShoppingCart} className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  </div>
                </div>
                <div className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                  132
                </div>
                <div className="flex items-center text-sm">
                  <SafeIcon icon={FiIcons.FiArrowUp} className="w-4 h-4 text-green-500 mr-1" />
                  <span className="text-green-500 font-medium">8% increase</span>
                  <span className="text-gray-500 dark:text-gray-400 ml-1">vs last month</span>
                </div>
              </div>
              
              <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Average Order
                  </h3>
                  <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                    <SafeIcon icon={FiIcons.FiTrendingUp} className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  </div>
                </div>
                <div className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                  $59.43
                </div>
                <div className="flex items-center text-sm">
                  <SafeIcon icon={FiIcons.FiArrowUp} className="w-4 h-4 text-green-500 mr-1" />
                  <span className="text-green-500 font-medium">3% increase</span>
                  <span className="text-gray-500 dark:text-gray-400 ml-1">vs last month</span>
                </div>
              </div>
            </div>
            
            <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                Monthly Sales
              </h3>
              <div className="h-80">
                <div className="h-full flex items-end">
                  {salesData.map((item, index) => (
                    <div 
                      key={item.month} 
                      className="flex-1 flex flex-col items-center"
                    >
                      <div 
                        className="w-full max-w-[40px] bg-primary-500 rounded-t-lg relative group"
                        style={{ height: `${(item.sales / 2100) * 100}%` }}
                      >
                        <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                          ${item.sales}
                        </div>
                      </div>
                      <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                        {item.month}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Top Products
                </h3>
                <div className="space-y-4">
                  {products.slice(0, 3).map((product) => (
                    <div key={product.id} className="flex items-center">
                      <div className="w-12 h-12 rounded-md overflow-hidden mr-3">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                          {product.name}
                        </h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          ${product.price} · {product.sales} sold
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Sales by Product Type
                </h3>
                <div className="h-[200px] flex items-center justify-center">
                  {/* This would typically be a pie chart */}
                  <div className="flex space-x-6">
                    <div className="flex flex-col items-center">
                      <div className="w-32 h-32 rounded-full border-8 border-primary-500 flex items-center justify-center">
                        <span className="text-xl font-bold text-primary-500">45%</span>
                      </div>
                      <span className="mt-2 text-sm font-medium text-gray-600 dark:text-gray-300">Digital</span>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="w-32 h-32 rounded-full border-8 border-secondary-500 flex items-center justify-center">
                        <span className="text-xl font-bold text-secondary-500">35%</span>
                      </div>
                      <span className="mt-2 text-sm font-medium text-gray-600 dark:text-gray-300">Print</span>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="w-32 h-32 rounded-full border-8 border-gray-400 flex items-center justify-center">
                        <span className="text-xl font-bold text-gray-400">20%</span>
                      </div>
                      <span className="mt-2 text-sm font-medium text-gray-600 dark:text-gray-300">Physical</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      
      case 'customers':
        return (
          <div className="text-center p-12 text-gray-600 dark:text-gray-400">
            <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiUsers} className="w-8 h-8 text-gray-400" />
            </div>
            <h2 className="text-xl font-medium text-gray-900 dark:text-white mb-2">
              Customer Management
            </h2>
            <p className="mb-6 max-w-md mx-auto">
              Customer management features are coming soon. Here you'll be able to view and manage customer information, track purchase history, and more.
            </p>
            <Button variant="outline">
              Coming Soon
            </Button>
          </div>
        );
      
      default:
        return (
          <div className="text-center p-12 text-gray-600 dark:text-gray-400">
            Select a tab to view content
          </div>
        );
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Store Manager
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Manage your products, orders, and store settings
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="flex overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`flex items-center py-4 px-6 border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:border-gray-300 dark:hover:border-gray-600'
                }`}
                onClick={() => setActiveTab(tab.id)}
              >
                <SafeIcon icon={tab.icon} className="w-5 h-5 mr-2" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          {renderTabContent()}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default StoreManager;